import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SaldoCliente } from '@shared/models/controlCredito/SaldoCliente';
import { environment } from '@environments';
import { Estado } from '@shared/models/controlCredito/Estado';
import { NotificacionSistemaDTO } from '@shared/models/notificaciones/NotificacionSistemaDTO';
import { NivelControlCredito, UsuarioNivelControlCredito } from '@shared/models/controlCredito/NivelControlCredito';
import { HoraBloqueoClientes } from '@shared/models/controlCredito/HoraBloqueoClientes';
import { AprobacionRechazoSolicitudBloqueo, ConfiguracionBloqueoDTO } from '@shared/models/controlCredito/ConfiguracionBloqueoDTO';
import { SolicitudCrediticiaDTO } from '@shared/models/controlCredito/SolicitudCrediticiaDTO';
import { SolicitudBloqueoDTO } from '@shared/models/controlCredito/Solicitante';
import { SolicitudCliente } from "@shared/models/controlCredito/SolicitudCliente";
import { AccionEliminar } from '@shared/enums/AccionEliminar';
import { RespuestaModificaciones } from '@controlCredito/reportes/reporte-autorizaciones/reporte-autorizaciones.component';
import { EstadoNotificacion } from "@shared/enums/EstadoNotificacion";
import { UsuarioDTO } from "@shared/models/notificaciones";

@Injectable({
  providedIn: 'root'
})
export class ControlCreditoService {

  constructor(private http: HttpClient) { }

  baseURL = environment.apiServer.clientesBaseUrl;
  notificacionesURL = environment.apiServer.notificacionesBaseUrl
  private saldoURL = this.baseURL + '/SaldosV1';
  anulando = signal(false);

  getSaldosFiltro(filtros, page: number, pageSize: number, busqueda: string): Observable<SaldoCliente[]> {
    if (!busqueda) busqueda = "";
    return this.http.put<SaldoCliente[]>(`${this.baseURL}/ClientesV2/ListadoClientesControlCredito?page=${page}&limit=${pageSize}`, filtros)
  }
  //Se utiliza para el manejo de estados (anulando o no) en solicitudes pendientes
  setAnulando(valor: boolean) {
    this.anulando.set(valor);
  }

  getReporteAutorizaciones(fechaDesde: string, fechaHasta: string): Observable<RespuestaModificaciones> {
      return this.http.get<RespuestaModificaciones>(`${this.baseURL}/ClientesV2/ModificacionesControlCredito?fechaDesde=${fechaDesde}&fechaHasta=${fechaHasta}`)
  }

  putEstadoCuenta(estado, clienteId): Observable<Estado> {
    return this.http.put<Estado>(`${this.baseURL}/ClientesV2/CambiarEstado/${clienteId}`, estado);
  }

  postSolicitarCredito(envioSolicitud: SolicitudCrediticiaDTO): Observable<SolicitudCrediticiaDTO> {
    return this.http.post<SolicitudCrediticiaDTO>(`${this.baseURL}/SaldosV1/CrearSolicitudCrediticiaParaCliente`, envioSolicitud);
  }

  actualizarSaldo(tipoDoc, numDoc) {
    return this.http.put<SaldoCliente>(`${this.baseURL}/SaldosV1/ActualizarSaldo?tipoDocumento=${tipoDoc}&numeroDocumento=${numDoc}`, {})
  }

  getAerovales(id) {
    return this.http.get<any[]>(`${this.saldoURL}/Aerovales/Clientes?ClienteId=${id}`);
  }

  getHistorialBloqueos(clienteId) {
    return this.http.get<Estado[]>(`${this.baseURL}/ClientesV2/EstadosCliente/${clienteId}`)
  }

  crearNotificacion(notificacion) {
    return this.http.post<NotificacionSistemaDTO>(`${this.notificacionesURL}/NotificacionesSistemasV1`, notificacion)
  }

  crearNotificacionSaldos(clienteId: string, saldo: EstadoNotificacion, notificacion){
    return this.http.post<NotificacionSistemaDTO>(`${this.baseURL}/ClientesV2/AsignarNotificacionSaldoSuperado/${clienteId}?saldo=${saldo}`, notificacion)
  }

  actualizarNotificacion(notificacion, id) {
    return this.http.put<NotificacionSistemaDTO>(`${this.notificacionesURL}/NotificacionesSistemasV1/${id}`, notificacion)
  }

  getNiveles(): Observable<NivelControlCredito[]> {
    return this.http.get<NivelControlCredito[]>(this.baseURL + '/NivelesV1/NivelesControlCredito');
  }

  crearNuevoNivel(nivel): Observable<NivelControlCredito> {
    return this.http.post<NivelControlCredito>(this.baseURL + '/NivelesV1/NivelControlCredito', nivel);
  }

  editarNivel(nivel): Observable<NivelControlCredito> {
    return this.http.put<NivelControlCredito>(this.baseURL + '/NivelesV1/NivelControlCredito', nivel);
  }

  obtenerHoraBloqueoClientes(): Observable<HoraBloqueoClientes> {
    return this.http.get<HoraBloqueoClientes>(this.baseURL + '/ConfiguracionesV1/HoraBloqueoClientes');
  }

  modificarHoraBloqueoClientes(hora: HoraBloqueoClientes): Observable<HoraBloqueoClientes> {
    return this.http.put<HoraBloqueoClientes>(this.baseURL + '/ConfiguracionesV1/HoraBloqueoClientes', hora)
  }

  descargarExcelActualizacionSaldosClientes(saldoClientes) {
    const url = this.baseURL + '/ExportarV1/SaldosClientes';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
    return this.http.put(url, saldoClientes, { headers: headers, responseType: 'blob' as 'json' });
  }

  descargarExcelActualizacionAerovales(aerovales) {
    const url = this.baseURL + '/ExportarV1/Aerovales';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
    return this.http.put(url, aerovales, { headers: headers, responseType: 'blob' as 'json' });
  }

  agregarUsuario(usuario: UsuarioNivelControlCredito): Observable<NivelControlCredito> {
    return this.http.post<NivelControlCredito>(this.baseURL + '/NivelesV1/AgregarUsuarioANivelControlCredito', usuario)
  }

  agregarDelegacion(usuario: UsuarioNivelControlCredito): Observable<NivelControlCredito> {
    return this.http.post<NivelControlCredito>(this.baseURL + '/NivelesV1/AgregarDelegacionANivelControlCredito', usuario)
  }

  eliminarDelegacion(usuario: UsuarioNivelControlCredito) {
    return this.http.delete<UsuarioNivelControlCredito>(
      this.baseURL + '/NivelesV1/QuitarDelegacionDeNivelControlCredito',
      {
        body: usuario
      }
    );
  }

  eliminarUsuario(usuario: UsuarioNivelControlCredito) {
    return this.http.delete<UsuarioNivelControlCredito>(this.baseURL + '/NivelesV1/QuitarUsuarioDeNivelControlCredito',
    {
      body: usuario
    })
  }

  eliminarSolicitud(solicitudId: string, accionEliminar: AccionEliminar, usuario: UsuarioDTO) {
    return this.http.put(this.saldoURL + `/EliminarSolicitudSegunAccion/${solicitudId}?eliminar=${accionEliminar}`, usuario);
  }

  configurarBloqueoCliente(clienteId: string, configuracion: ConfiguracionBloqueoDTO) {
    return this.http.put(this.baseURL + `/ClientesV2/ConfiguracionBloqueoCliente/${clienteId}`, configuracion);
  }

  finalizarSolicitud(bodyAprobador: SolicitudCliente){
    return this.http.put(this.baseURL + `/ClientesV2/AprobarRechazarSolicitudesCrediticias/`, bodyAprobador);
  }

  obtenerSolicitudesBloqueo(numeroDocumento, esAprobador): Observable<SolicitudBloqueoDTO[]> {
    return this.http.get<SolicitudBloqueoDTO[]>(this.baseURL + `/ClientesV2/SolicitudesConfiguracionBloqueo?numeroDocumento=${numeroDocumento}&esAprobador=${esAprobador}`);
  }

  eliminarSolicitudBloqueo(solicitudId: string, accionEliminar: AccionEliminar) {
    return this.http.delete(this.baseURL + `/ClientesV2/SolicitudConfiguracionBloqueo/${solicitudId}?eliminar=${accionEliminar}`)
  }

  aprobarRechazarConfiguracionBloqueo(solicitud: AprobacionRechazoSolicitudBloqueo, esAprobacion: boolean) {
    return this.http.put(this.baseURL + `/ClientesV2/AprobarRechazarSolicitudConfiguracionBloqueo/${esAprobacion}`, solicitud)
  }
}
